// Gioco.tsx — tutto ciò che succede su /c/<codice>: invito, nome, permessi della fotocamera, poi la partita.
// Il link non avvia mai la chiamata da solo: chi lo apre vede chi lo sfida e sceglie se accettare.
import { AvvisoSospensione } from "../componenti/AvvisoSospensione.tsx";
import { ConsensoVolto, serveConsenso } from "../gioco/ConsensoVolto.tsx";
import { useAccount } from "../servizi/account.ts";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Faccina } from "../componenti/Faccina.tsx";
import { Avatar } from "../componenti/Avatar.tsx";
import { Partita } from "../gioco/Partita.tsx";
import { CampoNome, useNomeGiocatore } from "../gioco/CampoNome.tsx";
import { apriFotocamera, type ErroreFotocamera } from "../gioco/video.ts";
import { preparaRilevamento } from "../gioco/rilevamento.ts";
import { stanza, useStanza } from "../gioco/stanza.ts";
import { leggiSfida, tokenSalvato, type InfoSfida } from "../servizi/sfide.ts";
import { ErroreRete } from "../servizi/api.ts";
import { leggiPreferenza, salvaPreferenza } from "../servizi/preferenze.ts";
import type { ControlloTema } from "../temi/useTema.ts";
import { PREFERENZE } from "../costanti.ts";

type Passo =
  | { p: "caricamento" }
  | { p: "invito"; info: InfoSfida }
  | { p: "volto" }
  | { p: "fotocamera" }
  | { p: "errore_fotocamera"; errore: ErroreFotocamera }
  | { p: "stanza"; flusso: MediaStream }
  | { p: "errore"; codice: string };

interface Props {
  codice: string;
  tema: ControlloTema;
  onEsci: () => void;
  onNuovaSfida: () => void;
}

export function Gioco({ codice, tema, onEsci, onNuovaSfida }: Props) {
  const { t } = useTranslation();
  const [passo, setPasso] = useState<Passo>({ p: "caricamento" });
  const [nome, setNome] = useNomeGiocatore(); // v039: anche il nome dell'account su un telefono nuovo
  const account = useAccount();
  const [accettaDopo, setAccettaDopo] = useState(false);
  const st = useStanza();
  const flussoRif = useRef<MediaStream | null>(null);

  const chiudiFotocamera = useCallback(() => {
    flussoRif.current?.getTracks().forEach((tr) => tr.stop());
    flussoRif.current = null;
  }, []);

  const apri = useCallback(async () => {
    // v021: prima della prima partita la spiegazione dell'analisi del volto, con la spunta. v040: e «Le tue scelte».
    if (serveConsenso()) {
      setPasso({ p: "volto" });
      return;
    }
    setPasso({ p: "fotocamera" });
    void preparaRilevamento(); // MediaPipe si carica mentre si aspetta
    try {
      const f = await apriFotocamera();
      if (!montato.current) {
        // Uscito mentre il telefono chiedeva il permesso: niente fotocamera accesa, niente ingresso nella sfida.
        f.getTracks().forEach((tr) => tr.stop());
        return;
      }
      flussoRif.current = f;
      // Con il token il nome non serve (il server tiene quello della sfida), ma il messaggio lo vuole non vuoto: chi apre
      // la «Prova da solo» senza aver mai scritto un nome manda «Tu».
      const token = tokenSalvato(codice);
      stanza.entra(codice, leggiPreferenza(PREFERENZE.nome) || nome || (token ? t("prova.da_solo_nome") : ""), token);
      setPasso({ p: "stanza", flusso: f });
    } catch (e) {
      setPasso({ p: "errore_fotocamera", errore: e as ErroreFotocamera });
    }
  }, [codice, nome, t]);

  useEffect(() => {
    if (!accettaDopo || account.tipo === "caricamento") return;
    setAccettaDopo(false);
    const n = (nome.trim() || (account.tipo === "registrato" ? account.io.nome : "")).trim();
    if (!n) return; // niente nome nemmeno dall'account: resta il campo da riempire
    salvaPreferenza(PREFERENZE.nome, n);
    void apri();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accettaDopo, account.tipo]);

  // Chi ha già il token di questa sfida (l'ha creata, o sta rientrando) va diretto alla partita.
  // Una volta sola, anche quando React (in sviluppo) esegue gli effetti due volte.
  const avviato = useRef(false);
  useEffect(() => {
    if (avviato.current) return;
    avviato.current = true;
    if (tokenSalvato(codice)) {
      void apri();
      return;
    }
    leggiSfida(codice)
      .then((info) => setPasso({ p: "invito", info }))
      .catch((e: unknown) =>
        setPasso({ p: "errore", codice: e instanceof ErroreRete && e.stato === 404 ? "sfida_non_trovata" : e instanceof ErroreRete ? e.codice : "server" }),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codice]);

  // Uscendo davvero dalla schermata (anche con il tasto indietro): fotocamera spenta, fuori dalla stanza.
  // Il controllo dopo un attimo distingue l'uscita vera dal doppio montaggio di React in sviluppo.
  const montato = useRef(false);
  useEffect(() => {
    montato.current = true;
    return () => {
      montato.current = false;
      setTimeout(() => {
        if (montato.current) return;
        chiudiFotocamera();
        if (stanza.stato.passo !== "fuori") stanza.esci();
      }, 0);
    };
  }, [chiudiFotocamera]);

  const esci = useCallback(() => {
    stanza.esci();
    chiudiFotocamera();
    onEsci();
  }, [chiudiFotocamera, onEsci]);

  if (passo.p === "stanza" && st.passo === "errore") {
    // v040: partita chiusa a metà perché uno dei due ha bloccato l'altro
    if (st.errore === "bloccato" && st.ruolo) return <Messaggio tema={tema} testo={`${t("gioco.bloccato_titolo")}. ${t("gioco.bloccato_testo")}`} onHome={esci} />;
    if (st.errore === "bloccato")
      return (
        <Messaggio tema={tema} testo="" onHome={esci}>
          <AvvisoSospensione seNo={<p className="schermata-testo" role="alert">{t("gioco.errore_bloccato")}</p>} />
        </Messaggio>
      );
    return <Messaggio tema={tema} testo={t(`gioco.errore_${st.errore ?? "generico"}`)} onHome={esci} />;
  }
  if (passo.p === "stanza") {
    return <Partita codice={codice} flusso={passo.flusso} onEsci={esci} onNuovaSfida={() => { esci(); onNuovaSfida(); }} />;
  }
  if (passo.p === "errore") {
    return <Messaggio tema={tema} testo={t(`gioco.errore_${passo.codice}`, { defaultValue: t("gioco.errore_generico") })} onHome={onEsci} />;
  }
  if (passo.p === "invito") {
    return (
      <div className="pagina invito ld-pagina">
        <div className="ld-alone" aria-hidden="true" />
        <main className="invito-centro">
          {/* v019: la faccina che ha scelto chi ti sfida (o quella predefinita). */}
          <Avatar quale={passo.info.avatar ?? null} dimensione={170} anello classe="ld-invito-chi" />
          <h1 className="invito-titolo">{t("gioco.invito_titolo", { nome: passo.info.nome })}</h1>
          <p className="invito-testo">{t("gioco.invito_testo")}</p>
          <form
            className="invito-modulo"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              // v040: con l'account ancora in caricamento il nome arriva tra un attimo: si accetta appena c'è
              if (!nome.trim() && account.tipo === "caricamento") {
                setAccettaDopo(true);
                return;
              }
              if (!nome.trim()) {
                (e.currentTarget.querySelector("input") as HTMLInputElement | null)?.reportValidity();
                return;
              }
              salvaPreferenza(PREFERENZE.nome, nome.trim());
              void apri();
            }}
          >
            <CampoNome valore={nome} onCambia={setNome} />
            <button type="submit" className="pulsante pulsante-primario" disabled={accettaDopo} aria-busy={accettaDopo}>
              {t("gioco.accetta_sfida")}
            </button>
            <button type="button" className="pulsante pulsante-secondario" onClick={onEsci}>
              {t("gioco.no_grazie")}
            </button>
          </form>
        </main>
      </div>
    );
  }
  if (passo.p === "volto") return <ConsensoVolto onAccettato={() => void apri()} onEsci={esci} />;
  if (passo.p === "errore_fotocamera") {
    return (
      <Messaggio tema={tema} testo={t(`gioco.fotocamera_${passo.errore}`)} onHome={onEsci}>
        <button type="button" className="pulsante pulsante-primario" onClick={() => void apri()}>
          {t("gioco.riprova")}
        </button>
      </Messaggio>
    );
  }
  return (
    <div className="pagina schermata-semplice" aria-busy="true">
      <Faccina tema={tema.tema} />
      <p className="schermata-testo">{passo.p === "fotocamera" ? t("gioco.fotocamera_richiesta") : t("generale.caricamento")}</p>
    </div>
  );
}

function Messaggio({ tema, testo, onHome, children }: { tema: ControlloTema; testo: string; onHome: () => void; children?: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="pagina schermata-semplice">
      <Faccina tema={tema.tema} />
      {testo && (
        <p className="schermata-testo" role="alert">
          {testo}
        </p>
      )}
      <div className="azioni-colonna">
        {children}
        <button type="button" className="pulsante pulsante-secondario" onClick={onHome}>
          {t("gioco.torna_home")}
        </button>
      </div>
    </div>
  );
}
